import { useEffect, useRef, useState } from 'react';
import { PACKAGES } from '@aslgo/shared';
import { useAppStore } from '../store/useAppStore.js';
import { createSetupIntent } from '../api/client.js';
import { usePackagePurchase } from '../hooks/usePackagePurchase.js';
import { PurchaseModal } from './PurchaseModal.js';
import { CardOnFileModal } from './CardOnFileModal.js';
import { ConfirmPurchaseDialog } from './ConfirmPurchaseDialog.js';
import { EnterpriseContactRow } from './EnterpriseContactRow.js';
import { AlertIcon, CheckIcon } from '../icons.js';

export function AddMinutesPanel() {
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  const setSessionMinutes = useAppStore((s) => s.setSessionMinutes);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const {
    card, setCard, submitting, error: purchaseError, info: purchaseInfo, purchase, buy, onPurchased, closePurchase,
    pendingBuy, confirmBuy, cancelBuy,
  } = usePackagePurchase(setSessionMinutes);

  // iOS "Buy minutes" hand-off (App.tsx consumeBuyParam → store.buyIntent):
  // bring the wallet into view and, with no card on file yet, put the
  // card-on-file modal up first so the package purchase that follows can use
  // the saved card. Waits for the card lookup (undefined = still loading).
  const buyIntent = useAppStore((s) => s.buyIntent);
  const setBuyIntent = useAppStore((s) => s.setBuyIntent);
  const panelRef = useRef<HTMLElement>(null);
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  useEffect(() => {
    if (!buyIntent || card === undefined) return;
    panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    if (card) { setBuyIntent(false); return; }
    let active = true;
    createSetupIntent()
      .then((r) => { if (active) setSetupSecret(r.clientSecret); })
      .catch((e) => { if (active) { setError((e as Error).message); setBuyIntent(false); } });
    return () => { active = false; };
  }, [buyIntent, card, setBuyIntent]);

  async function onBuy() {
    if (!selected) { setError('Pick a package first.'); return; }
    setError(null); setInfo(null);
    const pkg = PACKAGES.find((p) => p.id === selected)!;
    await buy(pkg);
    setSelected(null);
  }

  const noticeError = error ?? purchaseError;
  const noticeInfo = noticeError ? null : (info ?? purchaseInfo);

  return (
    <aside ref={panelRef} className="card p-6 sm:p-7 has-grain">
      <div><div className="text-eyebrow mb-2">Wallet</div><h2 className="h-card text-ink-900 dark:text-ink-100">Session minutes</h2></div>
      <div className="mt-5 pb-6 border-b border-ink-200 dark:border-white/[0.06]">
        <div className="flex items-baseline gap-3">
          <span className="font-mono tabular-nums text-6xl font-semibold leading-none text-ink-900 dark:text-ink-100">{sessionMinutes}</span>
          <span className="text-[0.95rem] text-ink-500 dark:text-ink-400">{sessionMinutes === 1 ? 'minute' : 'minutes'} on hand</span>
        </div>
        <p className="hint mt-2">Buy a package — the balance carries over.</p>
      </div>
      <div className="mt-6 flex flex-col gap-2">
        <div className="field-label mb-1">Choose a package</div>
        {PACKAGES.map((p) => (
          <label key={p.id} className={`flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer transition
            ${selected === p.id ? 'border-brand ring-1 ring-brand' : 'border-ink-200 dark:border-white/10'}`}>
            <span className="flex items-center gap-3">
              <input type="radio" name="pkg" checked={selected === p.id} onChange={() => setSelected(p.id)} />
              <span>
                <span className="font-semibold text-ink-900 dark:text-ink-100">{p.name}</span>
                <span className="block text-[0.8rem] text-ink-600 dark:text-ink-400">{p.credits} credit{p.credits === 1 ? '' : 's'} · {p.minutes} minutes</span>
              </span>
            </span>
            <span className="font-semibold text-ink-900 dark:text-ink-100">${p.priceDollars.toLocaleString()}</span>
          </label>
        ))}
        <EnterpriseContactRow />
        <button type="button" className="btn-primary w-full justify-center mt-2" onClick={() => { void onBuy(); }} disabled={submitting || !selected}>
          {submitting ? 'Purchasing…' : 'Buy package'}
        </button>
      </div>
      {noticeInfo ? (<div className="mt-4 inline-flex items-start gap-2 px-3 py-2 rounded-lg text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 text-[0.9rem]"><CheckIcon className="shrink-0 mt-0.5" /><span>{noticeInfo}</span></div>) : null}
      {noticeError ? (<div className="notice-error mt-4"><AlertIcon className="shrink-0 mt-0.5" /><span>{noticeError}</span></div>) : null}
      {purchase ? (
        <PurchaseModal
          clientSecret={purchase.clientSecret}
          onPurchased={onPurchased}
          onClose={closePurchase}
        />
      ) : null}
      {pendingBuy && card ? (
        <ConfirmPurchaseDialog pkg={pendingBuy} card={card} busy={submitting} onConfirm={confirmBuy} onCancel={cancelBuy} />
      ) : null}
      {setupSecret ? (
        <CardOnFileModal
          clientSecret={setupSecret}
          onSaved={(saved) => { setCard(saved); setSetupSecret(null); setBuyIntent(false); setInfo('Card saved. Pick a package below.'); }}
          onClose={() => { setSetupSecret(null); setBuyIntent(false); }}
        />
      ) : null}
    </aside>
  );
}
